import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  User,
  Structure,
  StructureRequest,
  Procedure,
  ProcedureRequest,
  Tache,
  TacheRequest,
  Ponderation,
  PonderationRequest,
  DashboardStats,
  AdminAnalytics,
  UserRequest,
  PageResponse,
  PhaseScore,
  PhaseReference,
  PhaseReferenceRequest,
  AppNotification,
} from '../types';

export type { PhaseScore };

const API_BASE_URL =
  process.env.REACT_APP_API_URL || " https://backend.suividemat.gov.bf/api";

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Intercepteur pour ajouter le token JWT
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Intercepteur pour gérer les erreurs
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // ==================== AUTH ====================
  async register(data: RegisterRequest): Promise<void> {
    await this.api.post('/auth/register', data);
  }

  async verifyEmail(token: string): Promise<void> {
    await this.api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
  }

  async resendVerification(email: string): Promise<void> {
    await this.api.post('/auth/resend-verification', { email });
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/login', credentials);
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get<User>('/auth/me');
    return response.data;
  }

  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  }

  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  }

  // ==================== STRUCTURES ====================
  async getStructures(params?: { search?: string }): Promise<Structure[]> {
    const query = new URLSearchParams();
    if (params?.search) query.append('search', params.search);
    const response = await this.api.get<Structure[]>(`/structures${query.toString() ? `?${query.toString()}` : ''}`);
    return response.data;
  }

  async getStructuresPage(params?: { search?: string; page?: number; size?: number }): Promise<PageResponse<Structure>> {
    const query = new URLSearchParams();
    if (params?.search) query.append('search', params.search);
    if (params?.page !== undefined) query.append('page', params.page.toString());
    if (params?.size !== undefined) query.append('size', params.size.toString());
    const response = await this.api.get<PageResponse<Structure>>(`/structures?${query.toString()}`);
    return response.data;
  }

  async getStructure(id: string): Promise<Structure> {
    const response = await this.api.get<Structure>(`/structures/${id}`);
    return response.data;
  }

  async createStructure(data: StructureRequest): Promise<Structure> {
    const response = await this.api.post<Structure>('/structures', data);
    return response.data;
  }

  async updateStructure(id: string, data: StructureRequest): Promise<Structure> {
    const response = await this.api.put<Structure>(`/structures/${id}`, data);
    return response.data;
  }

  async deleteStructure(id: string): Promise<void> {
    await this.api.delete(`/structures/${id}`);
  }

  // ==================== PROCEDURES ====================
  async getProcedures(params?: {
    structureId?: string;
    structureBeneficiaireId?: string;
    annee?: number;
    search?: string;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: string;
  }): Promise<PageResponse<Procedure>> {
    const query = new URLSearchParams();
    if (params?.structureId) query.append('structureId', params.structureId);
    if (params?.structureBeneficiaireId) query.append('structureBeneficiaireId', params.structureBeneficiaireId);
    if (params?.annee) query.append('annee', params.annee.toString());
    if (params?.search) query.append('search', params.search);
    if (params?.page !== undefined) query.append('page', params.page.toString());
    if (params?.size !== undefined) query.append('size', params.size.toString());
    if (params?.sortBy) query.append('sortBy', params.sortBy);
    if (params?.sortDir) query.append('sortDir', params.sortDir);
    const response = await this.api.get<PageResponse<Procedure>>(`/procedures?${query.toString()}`);
    return response.data;
  }

  async getProcedure(id: string): Promise<Procedure> {
    const response = await this.api.get<Procedure>(`/procedures/${id}`);
    return response.data;
  }

  async createProcedure(data: ProcedureRequest): Promise<Procedure> {
    const response = await this.api.post<Procedure>('/procedures', data);
    return response.data;
  }

  async updateProcedure(id: string, data: ProcedureRequest): Promise<Procedure> {
    const response = await this.api.put<Procedure>(`/procedures/${id}`, data);
    return response.data;
  }

  async deleteProcedure(id: string): Promise<void> {
    await this.api.delete(`/procedures/${id}`);
  }

  async getDistinctYears(): Promise<number[]> {
    const response = await this.api.get<number[]>('/procedures/years');
    return response.data;
  }

  // ==================== TACHES ====================
  async getTachesByProcedure(procedureId: string): Promise<Tache[]> {
    const response = await this.api.get<Tache[]>(`/taches/procedure/${procedureId}`);
    return response.data;
  }

  async getTache(id: string): Promise<Tache> {
    const response = await this.api.get<Tache>(`/taches/${id}`);
    return response.data;
  }

  async createTache(data: TacheRequest): Promise<Tache> {
    const response = await this.api.post<Tache>('/taches', data);
    return response.data;
  }

  async updateTache(id: string, data: TacheRequest): Promise<Tache> {
    const response = await this.api.put<Tache>(`/taches/${id}`, data);
    return response.data;
  }

  async updateTacheEtat(id: string, etat: string): Promise<Tache> {
    const response = await this.api.patch<Tache>(`/taches/${id}/etat`, { etat });
    return response.data;
  }

  async deleteTache(id: string): Promise<void> {
    await this.api.delete(`/taches/${id}`);
  }

  async getTachesEnRetard(): Promise<Tache[]> {
    const response = await this.api.get<Tache[]>('/taches/en-retard');
    return response.data;
  }

  async getTachesProchesEcheance(jours: number = 7): Promise<Tache[]> {
    const response = await this.api.get<Tache[]>(`/taches/proches-echeance?jours=${jours}`);
    return response.data;
  }

  async getAllTachesGlobal(structureId?: string): Promise<Tache[]> {
    const q = structureId ? `?structureId=${structureId}` : '';
    const response = await this.api.get<Tache[]>(`/taches/global${q}`);
    return response.data;
  }

  // ==================== PONDERATIONS ====================
  async getPonderation(procedureId: string): Promise<Ponderation> {
    const response = await this.api.get<Ponderation>(`/ponderations/procedure/${procedureId}`);
    return response.data;
  }

  async updatePonderation(procedureId: string, data: PonderationRequest): Promise<Ponderation> {
    const response = await this.api.put<Ponderation>(`/ponderations/procedure/${procedureId}`, data);
    return response.data;
  }

  // ── Référentiel phases ────────────────────────────────────────

  async getPhaseReferences(): Promise<PhaseReference[]> {
    const response = await this.api.get<PhaseReference[]>('/ponderations/phases');
    return response.data;
  }

  async createPhaseReference(data: PhaseReferenceRequest): Promise<PhaseReference> {
    const response = await this.api.post<PhaseReference>('/ponderations/phases', data);
    return response.data;
  }

  async updatePhaseReference(id: string, data: PhaseReferenceRequest): Promise<PhaseReference> {
    const response = await this.api.put<PhaseReference>(`/ponderations/phases/${id}`, data);
    return response.data;
  }

  async deletePhaseReference(id: string): Promise<void> {
    await this.api.delete(`/ponderations/phases/${id}`);
  }

  // ==================== NOTIFICATIONS ====================
  async getNotifications(page = 0, size = 10): Promise<PageResponse<AppNotification>> {
    const response = await this.api.get<PageResponse<AppNotification>>(
      `/notifications?page=${page}&size=${size}`,
    );
    return response.data;
  }

  async getUnreadNotificationCount(): Promise<number> {
    const response = await this.api.get<{ count: number }>('/notifications/unread-count');
    return response.data.count;
  }

  async markNotificationAsRead(id: string): Promise<void> {
    await this.api.put(`/notifications/${id}/read`);
  }

  async markAllNotificationsAsRead(): Promise<void> {
    await this.api.put('/notifications/read-all');
  }

  async togglePhaseActivite(
    procedureId: string,
    phaseId: string,
    itemType: string,
    itemIndex: number,
  ): Promise<Ponderation> {
    const response = await this.api.put<Ponderation>(
      `/ponderations/procedure/${procedureId}/phases/${phaseId}/toggle`,
      { itemType, itemIndex },
    );
    return response.data;
  }

  // ==================== USERS ====================
  async getUsers(params?: { search?: string; page?: number; size?: number }): Promise<PageResponse<User>> {
    const query = new URLSearchParams();
    if (params?.search) query.append('search', params.search);
    if (params?.page !== undefined) query.append('page', params.page.toString());
    if (params?.size !== undefined) query.append('size', params.size.toString());
    const response = await this.api.get<PageResponse<User>>(`/users?${query.toString()}`);
    return response.data;
  }

  async getMyStructureUsers(): Promise<User[]> {
    const response = await this.api.get<User[]>('/users/ma-structure');
    return response.data;
  }

  async getUsersByStructure(structureId: string): Promise<User[]> {
    const response = await this.api.get<User[]>(`/users/structure/${structureId}`);
    return response.data;
  }

  async getResponsablesForStructure(structureId: string): Promise<User[]> {
    const response = await this.api.get<User[]>(`/users/responsables/${structureId}`);
    return response.data;
  }

  async getUser(id: string): Promise<User> {
    const response = await this.api.get<User>(`/users/${id}`);
    return response.data;
  }

  async createUser(data: UserRequest): Promise<User> {
    const response = await this.api.post<User>('/users', data);
    return response.data;
  }

  async updateUser(id: string, data: UserRequest): Promise<User> {
    const response = await this.api.put<User>(`/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await this.api.delete(`/users/${id}`);
  }

  async getPendingUsers(): Promise<User[]> {
    const response = await this.api.get<User[]>('/users/pending');
    return response.data;
  }

  async activateUser(id: string): Promise<User> {
    const response = await this.api.put<User>(`/users/${id}/activate`);
    return response.data;
  }

  async rejectUser(id: string): Promise<void> {
    await this.api.delete(`/users/${id}/reject`);
  }

  // ==================== STATISTICS ====================
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.api.get<DashboardStats>('/statistics/dashboard');
    return response.data;
  }

  // ==================== ANALYTICS ====================
  async getAdminAnalytics(): Promise<AdminAnalytics> {
    const response = await this.api.get<AdminAnalytics>('/analytics/admin');
    return response.data;
  }

  // ==================== PASSWORD ====================
  async forgotPassword(email: string): Promise<void> {
    await this.api.post('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<void> {
    await this.api.post('/auth/reset-password', { token, newPassword, confirmPassword });
  }

  async changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<void> {
    await this.api.post('/auth/change-password', { currentPassword, newPassword, confirmPassword });
  }
}

export const apiService = new ApiService();
export default apiService;
